import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { BrandMark } from '../components/BrandMark';
import { DISCORD_LOGIN_URL, useMe } from '../lib/auth';

/**
 * The only way in. Discord OAuth is the sole credential path — there is no password field anywhere
 * in this app, by design. The "Continue with Discord" button deliberately keeps Discord's own
 * brand blurple rather than the app's accent — it's a third-party identity affordance, and
 * recoloring it to match our theme would just make it look less trustworthy.
 */
export function Login() {
  const [searchParams] = useSearchParams();
  const { data: me, isPending } = useMe();

  if (isPending) {
    return (
      <main className="flex min-h-full items-center justify-center p-6 text-text-muted">
        <p>Checking your session…</p>
      </main>
    );
  }

  if (me) {
    return <Navigate to="/" replace />;
  }

  return (
    <main
      className="shell-grain flex min-h-full items-center justify-center bg-bg p-6 text-text"
      style={{
        backgroundImage: 'radial-gradient(720px 520px at 50% -10%, var(--shell-glow), transparent 70%)',
      }}
    >
      <div className="stagger-children w-full max-w-sm space-y-6 rounded-2xl border border-border bg-bg-elevated p-8 text-center">
        <div className="space-y-2">
          <div className="flex justify-center">
            <BrandMark />
          </div>
          <p className="text-sm text-text-muted">
            Daily, weekly and monthly tracking for your GMS roster.
          </p>
        </div>

        {searchParams.has('error') && (
          <p role="alert" className="rounded-lg border border-l-4 border-border border-l-danger bg-bg-elevated-hover px-3 py-2 text-sm text-text">
            Discord sign-in did not complete. Please try again.
          </p>
        )}

        {/* A plain anchor, not a router link: this leaves the SPA for discord.com and returns
            through Caddy to /login/oauth2/code/discord. */}
        <a
          href={DISCORD_LOGIN_URL}
          className="flex min-h-tap w-full items-center justify-center gap-2 rounded-lg bg-[#5865f2] px-4 py-3 text-base font-semibold text-white transition-colors hover:bg-[#4752c4]"
        >
          Continue with Discord
        </a>

        <p className="text-xs text-text-faint">
          We only request the <code>identify</code> scope — your Discord id, username and avatar. No
          email, no passwords.{' '}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-text-muted">
            What we store
          </Link>
          .
        </p>
      </div>
    </main>
  );
}
